/**
 * Rebuy dynamic bundle: show each product's image inside its own row.
 *
 * Rebuy renders the images as a separate strip (.rebuy-bundle__images) and
 * drops a product's .rebuy-bundle__image node as soon as its
 * .rebuy-product-checkbox is unchecked. That strip is rendered by Rebuy's own
 * Vue app, so instead of fighting its re-renders we keep a copy of every image
 * it has rendered and prepend it to the matching .rebuy-product-block. The
 * original strip stays in the DOM (hidden by CSS) and deselected rows are
 * dimmed by CSS via Rebuy's own .is-selected class. Selection state, pricing
 * and add-to-cart stay entirely Rebuy's job: we only ever add our own node.
 */

(function () {
  const WIDGET = '.rebuy-widget.widget-type-dynamic-bundle';
  const SOURCE_CLASS = 'jg-bundle-images--source';
  const THUMB_CLASS = 'jg-bundle-thumb';
  const PRODUCT_ID = /(?:^|\s)product-id-(\d+)/;

  let frame = 0;

  function productId(element) {
    const match = PRODUCT_ID.exec(element.className || '');
    return match ? match[1] : null;
  }

  function imageSrc(imageBlock) {
    const image = imageBlock.querySelector('img');
    return image ? image.getAttribute('src') : '';
  }

  function isThumb(node) {
    return !!(node && node.nodeType === 1 && node.classList.contains(THUMB_CLASS));
  }

  function inThumb(node) {
    const element = node.nodeType === 1 ? node : node.parentNode;
    return !!(element && element.closest && element.closest('.' + THUMB_CLASS));
  }

  /* Mutations caused by placing or updating our own thumbs must not re-trigger a sync. */
  function isOwnMutation(record) {
    if (inThumb(record.target)) return true;
    if (record.type !== 'childList') return false;

    const nodes = Array.prototype.slice
      .call(record.addedNodes)
      .concat(Array.prototype.slice.call(record.removedNodes));
    return nodes.length > 0 && nodes.every(isThumb);
  }

  function getState(bundle) {
    const source = bundle.querySelector('.rebuy-bundle__images');
    const items = bundle.querySelector('.rebuy-bundle__items');
    if (!source || !items) return null;

    const state = bundle._jgBundleImages || (bundle._jgBundleImages = { clones: {} });

    if (state.source !== source) {
      source.classList.add(SOURCE_CLASS);
      state.source = source;
    }

    state.items = items;
    return state;
  }

  function makeThumb(rendered) {
    const clone = rendered.cloneNode(true);
    // Keep Rebuy's strip styles and scripts off the copy; it is ours, not Rebuy's.
    clone.classList.remove('rebuy-bundle__image');
    clone.classList.add(THUMB_CLASS);
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach(function (node) {
      node.removeAttribute('id');
    });
    return clone;
  }

  function cacheRenderedImages(state) {
    const rendered = state.source.querySelectorAll('.rebuy-bundle__image');

    for (let i = 0; i < rendered.length; i++) {
      const id = productId(rendered[i]);
      if (!id) continue;

      const cached = state.clones[id];
      // Re-clone only when Rebuy renders a different image (e.g. variant swap).
      if (cached && imageSrc(cached) === imageSrc(rendered[i])) continue;

      const clone = makeThumb(rendered[i]);
      if (cached && cached.parentNode) {
        cached.parentNode.replaceChild(clone, cached);
      }
      state.clones[id] = clone;
    }
  }

  /* Prepend each cached image to its product row, re-inserting it whenever Rebuy re-renders the row. */
  function placeThumbs(state) {
    const blocks = state.items.querySelectorAll('.rebuy-product-block');
    const seen = {};

    for (let i = 0; i < blocks.length; i++) {
      const id = productId(blocks[i]);
      const clone = id && state.clones[id];
      if (!clone) continue;

      seen[id] = true;
      if (blocks[i].firstElementChild !== clone) {
        blocks[i].insertBefore(clone, blocks[i].firstChild);
      }
    }

    // Drop products Rebuy no longer offers in this bundle.
    Object.keys(state.clones).forEach(function (id) {
      if (seen[id]) return;
      const clone = state.clones[id];
      if (clone.parentNode) clone.parentNode.removeChild(clone);
      delete state.clones[id];
    });
  }

  function observe(bundle) {
    if (bundle._jgBundleObserver) return;

    const observer = new MutationObserver(function (records) {
      for (let i = 0; i < records.length; i++) {
        if (!isOwnMutation(records[i])) {
          schedule();
          return;
        }
      }
    });

    observer.observe(bundle, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src']
    });
    bundle._jgBundleObserver = observer;
  }

  function sync() {
    const bundles = document.querySelectorAll(WIDGET + ' .rebuy-bundle');

    for (let i = 0; i < bundles.length; i++) {
      const state = getState(bundles[i]);
      if (!state) continue;

      cacheRenderedImages(state);
      placeThumbs(state);
      observe(bundles[i]);
    }
  }

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = 0;
      sync();
    });
  }

  /* Rebuy injects its widgets asynchronously, so watch for them arriving. */
  new MutationObserver(function (records) {
    for (let i = 0; i < records.length; i++) {
      const added = records[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.closest(WIDGET) || node.querySelector(WIDGET)) {
          schedule();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('rebuy:widget.ready', schedule);
  document.addEventListener('rebuy:widget.rendered', schedule);
  schedule();
})();
